import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Paper, Step, StepLabel, Stepper } from '@mui/material';
import TransportClinicStep from 'components/transport/add-transport/transport-clinic-step/transport-clinic-step.component';
import TransportWhenStep from 'components/transport/add-transport/transport-when-step/transport-when-step.component';
import TransportDetailsStep from 'components/transport/add-transport/transport-details-step/transport-details-step.component';
import { useMutation } from '@apollo/client';
import { AddTransportContext, emptyTransportOrderReq } from '../transport/add-transport/add-transport-context';
import { TransportOrderRequest, TransportServices } from 'models/transport';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import moment from 'moment';
import { transportDateHelper } from 'utils/transport-date-helper';
import TransportLabStep from 'components/transport/add-transport/transport-lab-step/transport-lab-step.component';
import { createTransportAsAdminTemplate } from 'apollo/templates/transport';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

const AdminAddTransport: FC = () => {
    const { t: translate } = useTranslation(['transport']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { type } = useParams<{ type: string }>();
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [transportOrderReq, setTransportOrderReq] = useState<TransportOrderRequest>(emptyTransportOrderReq());
    const shouldAddMoreRef = useRef(false);

    const [createTransportAsAdmin, { loading }] = useMutation(createTransportAsAdminTemplate, {
        onCompleted: () => {
            navigateOnPlacedOrder(shouldAddMoreRef.current);
        },
        onError: (error) => {
            const graphQLError = error.graphQLErrors?.[0];
            if (graphQLError?.message === 'TRANSPORT_ALREADY_EXISTS' && transportOrderReq.date) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'error',
                    message: translate('transport:TRANSPORT_ORDER_ALREADY_EXISTS'),
                });
                return;
            }
            showBoundary(error);
        },
    });

    useEffect(() => {
        const transportType = type === 'pickup' ? TransportServices.PICKUP : TransportServices.DELIVERY;
        setTransportOrderReq({ ...{ ...transportOrderReq, type: transportType } });
    }, [type]);

    const pageTitle = () => {
        return transportOrderReq.serviceCode === TransportServices.PICKUP ? translate('PICKUP') : translate('DELIVERY');
    };

    const getSteps = () => {
        return [translate('LAB'), translate('CLINIC'), translate('WHEN'), translate('DETAILS')];
    };

    const navigateNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const navigateBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const placeOrder = (shouldAddMore: boolean) => {
        shouldAddMoreRef.current = shouldAddMore;
        const dateZeroTime = calculateZeroDateTime();
        createTransportAsAdmin({
            variables: {
                serviceCode: transportOrderReq.serviceCode,
                clinic: transportOrderReq.clinic?._id,
                lab: transportOrderReq.lab?._id,
                transportDay: moment(dateZeroTime).format('YYYY-MM-DD'),
                manualPackageCount: transportOrderReq.manualPackageCount,
                comment: transportOrderReq.comment,
                internalMessage: transportOrderReq.internalMessage,
                deadline: transportOrderReq.deadline,
            },
        });
    };

    const calculateZeroDateTime = () => {
        if (transportOrderReq.date) {
            if (transportOrderReq.serviceCode === TransportServices.DELIVERY) {
                return new Date(transportDateHelper.nextWorkingDay(transportOrderReq.date, false).setHours(0, 0, 0, 0));
            }
            return new Date(transportOrderReq.date.setHours(0, 0, 0, 0));
        }
        throw new Error('Transport date not set');
    };

    const navigateOnPlacedOrder = (shouldAddMore: boolean) => {
        setAppToasterContent({ isOpen: true, severity: 'success', message: translate('transport:ORDER_PLACED') });
        if (shouldAddMore) {
            setTransportOrderReq(emptyTransportOrderReq(transportOrderReq.serviceCode));
            setActiveStep(0);
            return;
        }
        navigateTransportList();
    };

    const navigateTransportList = () => {
        navigate('/admin-transport/transport-orders');
    };

    const getStepContent = (stepIndex: number) => {
        switch (stepIndex) {
            case 0:
                return <TransportLabStep onNavigateNext={navigateNext} />;
            case 1:
                return <TransportClinicStep onNavigateNext={navigateNext} onNavigateBack={navigateBack} />;
            case 2:
                return <TransportWhenStep onNavigateNext={navigateNext} onNavigateBack={navigateBack} />;
            case 3:
                return (
                    <TransportDetailsStep loading={loading} onNavigateBack={navigateBack} onPlaceOrder={placeOrder} />
                );
            default:
                return 'Unknown stepIndex';
        }
    };

    return (
        <AddTransportContext.Provider value={{ transportOrderReq, setTransportOrderReq }}>
            <Box>
                <Box sx={{ margin: 'auto', maxWidth: '800px' }}>
                    <PageTitle headingStyle="h1" title={pageTitle()} />
                    <Paper>
                        <Box p={3}>
                            <Stepper activeStep={activeStep}>
                                {getSteps().map((label) => (
                                    <Step key={label}>
                                        <StepLabel>{label}</StepLabel>
                                    </Step>
                                ))}
                            </Stepper>
                            <Box mt={4}>{getStepContent(activeStep)}</Box>
                        </Box>
                    </Paper>
                    <Box mt={2} sx={{ textAlign: 'center' }}>
                        <Button onClick={() => navigateTransportList()}>{translate('CANCEL_ORDER')}</Button>
                    </Box>
                </Box>
            </Box>
            ;
        </AddTransportContext.Provider>
    );
};

export default AdminAddTransport;
